import React, { useState, useRef, useCallback, type DragEvent, useEffect } from 'react';
import { AiOutlineMenu, AiOutlineHome, AiOutlineSetting, AiOutlineUser, AiOutlinePlus, AiOutlineShareAlt, AiOutlineUpload, AiOutlineClose, AiOutlineFile, AiOutlineEdit, AiOutlineDelete, AiOutlineSearch, AiOutlineCloud, AiOutlineCalendar, AiOutlineQuestionCircle, AiOutlineBgColors, AiOutlineDownload, AiOutlineInfoCircle, AiOutlineMail, AiOutlineWarning } from 'react-icons/ai';
import { IoClose, IoChevronDown, IoCaretForward } from 'react-icons/io5';
import { VscDebugRestart } from 'react-icons/vsc';
import TimeTable from './TimeTable';
import CourseSlotSelector from './CourseSlotSelector';
import TimeTableModal from './TimeTableModal';
import FacultyPreferenceModal from './FacultyPreferenceModal';
import ExportModal from './ExportModal';
import ImportPopover from './ImportPopover';
import CourseCards from './CourseCards';
import TimeTableSlotSelector from './TimeTableSlotSelector';
import { PALETTES, getNextColorIndex } from '../utils/colorUtils';
import useConfig from '../config';
import FacultyList from './FacultyList';
import { parseCsvData } from '../utils/csvParser';
import { useCampus } from '../contexts/CampusContext';

// Define slot conflicts centrally here or import from a shared utility
// const slotConflictPairs = [ 
// ... existing code ...
//   ['E2', 'L55'], ['C2', 'L56'], ['TA2', 'L57'], ['TF2', 'L58'], ['TDD2', 'L59']
// ];

interface Course {
  name: string;
  slots: string[];
  credits: number;
  colorIndex: number;
  facultyPreferences?: string[];
  facultyLabAssignments?: Array<{ facultyName: string; slots: string[] }>;
  creationMode?: 'standard' | 'custom';
}

interface Faculty {
  CODE: string;
  TITLE: string;
  TYPE: string;
  CREDITS: string;
  VENUE: string;
  SLOT: string;
  FACULTY: string;
}

interface DashboardProps {
  currentPage: string;
  setCurrentPage: (page: string) => void;
  isImportModalOpen: boolean;
  setIsImportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isExportModalOpen: boolean;
  setIsExportModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  importButtonRef: React.RefObject<HTMLButtonElement>;
  exportButtonRef: React.RefObject<HTMLButtonElement>;
}

// Define a type for the stored data
interface StoredData {
  courses: Course[];
  preferredSlot: 'standard' | 'custom';
  selectedPalette: keyof typeof PALETTES;
}

const STORAGE_KEY = 'ffcs_planner_data';
const DONT_SHOW_CLOSE_WARNING_KEY = 'ffcs_planner_dont_show_close_warning';

const Dashboard: React.FC<DashboardProps> = ({
  currentPage,
  setCurrentPage,
  isImportModalOpen,
  setIsImportModalOpen,
  isExportModalOpen,
  setIsExportModalOpen,
  importButtonRef,
  exportButtonRef
}): JSX.Element => {
  const config = useConfig();
  const slotConflictPairs = config.slotConflictPairs;

  // Load initial state from localStorage
  const loadInitialState = (): StoredData => {
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
    return {
      courses: [],
      preferredSlot: 'standard',
      selectedPalette: 'default'
    };
  };

  const initialState = loadInitialState();

  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [courses, setCourses] = useState<Course[]>(initialState.courses);
  const [isTimeTableModalOpen, setTimeTableModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempCourseData, setTempCourseData] = useState<{
    courseName: string;
    selectedSlots: string[];
    credits: number;
    facultyPreferences?: string[];
    includeLabCourse?: boolean;
    facultyLabAssignments?: Map<string, string[]>;
    hasLabComponent?: boolean;
    isSlashCommandSelection?: boolean;
  } | null>(null);
  const [isFacultyModalOpen, setIsFacultyModalOpen] = useState(false);
  const [editingCourseIndex, setEditingCourseIndex] = useState<number | null>(null);
  const [isTimeTableSlotModalOpen, setIsTimeTableSlotModalOpen] = useState(false);
  const prevScrollPos = useRef(0);
  const [facultySearchQuery, setFacultySearchQuery] = useState('');
  const [preferredSlot, setPreferredSlot] = useState<'standard' | 'custom'>(initialState.preferredSlot);
  const [selectedPalette, setSelectedPalette] = useState<keyof typeof PALETTES>(initialState.selectedPalette);
  const [tempColorIndex, setTempColorIndex] = useState<number>(0);
  const [isPaletteDropdownOpen, setIsPaletteDropdownOpen] = useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [infoPopupOpen, setInfoPopupOpen] = useState<string | null>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isCloseWarningModalOpen, setIsCloseWarningModalOpen] = useState(false);
  const [dontShowCloseWarning, setDontShowCloseWarning] = useState(() => {
    return localStorage.getItem(DONT_SHOW_CLOSE_WARNING_KEY) === 'true';
  });

  // Add import error state
  const [importError, setImportError] = useState<string | undefined>();

  const [facultyData, setFacultyData] = useState<Faculty[]>([]);
  const { campus } = useCampus();

  useEffect(() => {
    let dataPath;
    switch (campus) {
      case 'Chennai':
        dataPath = import('../data/report_chennai.csv');
        break;
      case 'AP':
        dataPath = import('../data/report_ap.csv');
        break;
      default:
        dataPath = import('../data/report_vellore.csv');
    }

    dataPath.then(module => {
      fetch(module.default)
        .then(response => response.text())
        .then(text => {
          if (text.trim() === '') {
            setFacultyData([]);
            return;
          }
          const parsedData = parseCsvData(text) as Faculty[];
          setFacultyData(parsedData);
        }).catch(error => {
          console.error("Error fetching or parsing CSV data:", error);
          setFacultyData([]);
        });
    });
  }, [campus]);

  // Add click outside handler for help menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setIsHelpMenuOpen(false);
      }
    };

    if (isHelpMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHelpMenuOpen]);

  // Save data to localStorage whenever it changes
  useEffect(() => {
    try {
      const dataToSave: StoredData = {
        courses,
        preferredSlot,
        selectedPalette
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving data:', error);
    }
  }, [courses, preferredSlot, selectedPalette]);

  // Add a function to clear saved data
  const clearSavedData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setCourses([]);
      setPreferredSlot('standard');
      setSelectedPalette('default');
    } catch (error) {
      console.error('Error clearing saved data:', error);
    }
  };

  // Modify handleResetTable to use the modal
  const handleResetTable = () => {
    if (courses.length > 0) {
      setIsResetModalOpen(true);
    }
  };

  const handleConfirmReset = () => {
    clearSavedData();
    setIsResetModalOpen(false);
  };

  // Refs for the palette dropdown
  const paletteDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (paletteDropdownRef.current && !paletteDropdownRef.current.contains(event.target as Node)) {
        setIsPaletteDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [paletteDropdownRef]);

  const getBaseCourseName = (name: string) => name.replace(/ (Lab|Edit Lab|Edit Faculty|Add Lab)$/, '').trim();

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        handleFileImport(file);
      } else {
        alert('Please upload a CSV file');
      }
    }
  }, []);

  const handleFileImport = (file: File) => {
    // Reset error state on new import
    setImportError(undefined);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (!content) return;
      
      // Parse CSV content
      const lines = content.split('\n');
      const csvHeaders = lines[0].split(',');
      
      // Check if the CSV has the expected format
      const expectedHeaders = ['campus', 'courseName', 'slots', 'credits', 'colorIndex', 'facultyPreferences', 'preferredSlot', 'creationMode', 'facultyLabAssignments'];
      const isValidFormat = expectedHeaders.every(header => csvHeaders.includes(header));
      
      if (!isValidFormat) {
        setImportError('Invalid CSV format. Please use the exported CSV format.');
        return;
      }
      
      // Extract data from the CSV
      const data: {
        courses: Course[];
        preferredSlot: 'standard' | 'custom';
      } = {
        courses: [],
        preferredSlot: 'standard'
      };
      
      // Process each row
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue; // Skip empty lines
        
        const values = lines[i].split(',');
        const rowData: { [key: string]: any } = {};
        
        csvHeaders.forEach((header, index) => {
          rowData[header] = values[index];
        });

        // Check if the campus matches the current selected campus
        const currentCampus = localStorage.getItem('ffcs_planner_campus') || 'Vellore Campus';
        if (rowData['campus'] !== currentCampus) {
          setImportError(`This timetable is from ${rowData['campus']}. Please switch to that campus first before importing.`);
          return;
        }
        
        const courseCreationMode = rowData['creationMode'] === 'custom' ? 'custom' : 'standard';

        // Add course to data
        data.courses.push({
          name: rowData['courseName'],
          slots: rowData['slots'] ? rowData['slots'].split('|') : [],
          credits: parseInt(rowData['credits']),
          colorIndex: parseInt(rowData['colorIndex']) || 0,
          facultyPreferences: rowData['facultyPreferences'] ? rowData['facultyPreferences'].split('|').filter(Boolean).map((fp: string) => fp.toLowerCase()) : [],
          facultyLabAssignments: rowData['facultyLabAssignments'] 
            ? rowData['facultyLabAssignments'].split(';').filter(Boolean).map((assignment: string) => {
                const [facultyName, slotsString] = assignment.split(':');
                return {
                  facultyName: facultyName.toLowerCase(),
                  slots: slotsString ? slotsString.split('-') : []
                };
              })
            : undefined,
          creationMode: courseCreationMode
        });
        
        if (i === 1) {
          data.preferredSlot = courseCreationMode;
        }
      }
      
      // Only update state if there were no errors
      if (!importError) {
        // Update the state with the imported data
        setCourses(data.courses);
        setPreferredSlot(data.preferredSlot);
        
        // Close the import modal
        setIsImportModalOpen(false);
      }
    };
    
    reader.readAsText(file);
    
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const importDataFromCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    handleFileImport(file);
  };

  const handleAddFaculty = (data: {
    courseName: string;
    selectedSlots: string[];
    credits: number;
    colorIndex: number;
    hasLabComponent?: boolean;
    isSlashCommandSelection?: boolean;
  }) => {
    setTempCourseData({
      courseName: data.courseName,
      selectedSlots: data.selectedSlots,
      credits: data.credits,
      hasLabComponent: data.hasLabComponent,
      isSlashCommandSelection: data.isSlashCommandSelection
    });
    setTempColorIndex(data.colorIndex);
    setIsCourseModalOpen(false);
    setIsFacultyModalOpen(true);
  };

  const handleCourseSubmit = (data: { 
    courseName: string; 
    selectedSlots: string[]; 
    credits: number;
    facultyPreferences?: string[];
    includeLabCourse?: boolean;
    facultyLabAssignments?: Map<string, string[]>
  }) => {
    let updatedCourses = [...courses];
    const theoryFacultyPrefs = data.facultyPreferences || [];
    const facultyLabAssignmentsArray = data.facultyLabAssignments 
      ? Array.from(data.facultyLabAssignments.entries()).map(([facultyName, slots]) => ({ facultyName, slots }))
      : undefined;

    if (editingCourseIndex !== null) {
      const editingCourse = updatedCourses[editingCourseIndex];
      const oldCourseName = editingCourse.name;
      const isEditingTheory = !oldCourseName.endsWith(' Lab');
      
      // Update the current course being edited
      updatedCourses[editingCourseIndex] = { 
        ...editingCourse,
        name: data.courseName, 
        slots: data.selectedSlots, 
        credits: data.credits, 
        colorIndex: editingCourse.colorIndex,
        facultyPreferences: theoryFacultyPrefs,
        facultyLabAssignments: facultyLabAssignmentsArray || editingCourse.facultyLabAssignments
      };
      
      // If editing a theory course, update its lab course if it exists
      if (isEditingTheory) {
        const oldLabName = `${oldCourseName} Lab`;
        const newLabName = `${data.courseName} Lab`;
        const labIndex = updatedCourses.findIndex(c => c.name === oldLabName);
        
        if (labIndex !== -1) {
          updatedCourses[labIndex] = {
            ...updatedCourses[labIndex],
            name: newLabName,
            colorIndex: editingCourse.colorIndex
          };
        }
      }
      
      setEditingCourseIndex(null);
    } else {
      const newColorIndex = getNextColorIndex(courses);
      const newTheoryCourse: Course = {
        name: data.courseName, 
        slots: data.selectedSlots, 
        credits: data.credits,
        colorIndex: newColorIndex,
        facultyPreferences: theoryFacultyPrefs,
        creationMode: 'standard'
      };
      updatedCourses.push(newTheoryCourse);

      if (data.includeLabCourse) {
        const newLabCourse: Course = {
          name: `${data.courseName} Lab`,
          slots: data.selectedSlots,
          credits: 1,
          colorIndex: newColorIndex,
          facultyPreferences: theoryFacultyPrefs,
          facultyLabAssignments: facultyLabAssignmentsArray,
          creationMode: 'standard'
        };
        updatedCourses.push(newLabCourse);
      }
    }
    setCourses(updatedCourses);
  };

  const handleFacultyPreferenceSubmit = (
    facultyPreferences: string[], 
    includeLabCourse?: boolean, 
    facultyLabAssignmentsMap?: Map<string, string[]>
  ) => {
    const facultyLabAssignmentsArray = facultyLabAssignmentsMap
      ? Array.from(facultyLabAssignmentsMap.entries()).map(([facultyName, slots]) => ({ facultyName, slots }))
      : undefined;

    if (tempCourseData && tempCourseData.courseName.includes('Add Lab')) {
      const baseCourseName = getBaseCourseName(tempCourseData.courseName);
      const theoryCourseIndex = courses.findIndex(c => c.name === baseCourseName);

      if (theoryCourseIndex !== -1) {
        const theoryCourse = courses[theoryCourseIndex];
        const labCourse: Course = {
          name: `${baseCourseName} Lab`,
          slots: theoryCourse.slots, // Initially same as theory
          credits: 1, // Labs are typically 1 credit
          colorIndex: theoryCourse.colorIndex,
          facultyPreferences: facultyPreferences,
          facultyLabAssignments: facultyLabAssignmentsArray,
          creationMode: theoryCourse.creationMode,
        };
        
        // Add theory faculty preferences as well
        const updatedTheoryCourse = {
            ...theoryCourse,
            facultyPreferences: facultyPreferences,
            facultyLabAssignments: facultyLabAssignmentsArray,
        };

        setCourses(prevCourses => {
            const newCourses = [...prevCourses];
            newCourses[theoryCourseIndex] = updatedTheoryCourse;
            
            // Check if lab already exists to avoid duplicates
            const existingLabIndex = newCourses.findIndex(c => c.name === labCourse.name);
            if(existingLabIndex === -1) {
                newCourses.push(labCourse);
            }
            return newCourses;
        });
      }
      setIsFacultyModalOpen(false);
      setTempCourseData(null);
      setEditingCourseIndex(null);
      return; // Exit after handling
    }

    if (editingCourseIndex !== null) {
      // We are editing an existing course's faculty
      const originalCourse = courses[editingCourseIndex];
      const baseCourseName = getBaseCourseName(originalCourse.name);

      setCourses(prevCourses => {
        let updatedCourses = [...prevCourses];
        
        // Update the theory course
        updatedCourses[editingCourseIndex] = {
          ...originalCourse,
          facultyPreferences: facultyPreferences,
          facultyLabAssignments: facultyLabAssignmentsArray || originalCourse.facultyLabAssignments
        };

        // If a lab course needs to be added or updated
        if (includeLabCourse) {
          const labCourseName = `${baseCourseName} Lab`;
          const existingLabIndex = updatedCourses.findIndex(c => getBaseCourseName(c.name) === baseCourseName && c.name.endsWith(' Lab'));

          const labCourseData: Course = {
            name: labCourseName,
            slots: originalCourse.slots, // Labs share theory slots
            credits: 1,
            colorIndex: originalCourse.colorIndex,
            facultyPreferences: facultyPreferences,
            facultyLabAssignments: facultyLabAssignmentsArray,
            creationMode: originalCourse.creationMode
          };

          if (existingLabIndex !== -1) {
            // Update existing lab course
            updatedCourses[existingLabIndex] = labCourseData;
          } else {
            // Add new lab course
            updatedCourses.push(labCourseData);
          }
        }
        
        return updatedCourses;
      });

      setEditingCourseIndex(null);
      setTempCourseData(null);
      setIsFacultyModalOpen(false);

    } else if (tempCourseData) {
      // We are adding a new course (or at least, that's what this branch should handle)
      // This case might not be strictly needed if faculty are only added via editing a course that already exists.
      // For safety, let's assume it could be for adding a new course with faculty info right away.
      const baseCourseName = getBaseCourseName(tempCourseData.courseName);

      const newCourse: Course = {
        name: baseCourseName,
        slots: tempCourseData.selectedSlots,
        credits: tempCourseData.credits,
        colorIndex: tempColorIndex,
        facultyPreferences: facultyPreferences,
        facultyLabAssignments: facultyLabAssignmentsArray,
        creationMode: 'standard'
      };
      
      let updatedCourses = [...courses, newCourse];

      if (includeLabCourse) {
        const labCourse: Course = {
          name: `${baseCourseName} Lab`,
          slots: tempCourseData.selectedSlots,
          credits: 1,
          colorIndex: tempColorIndex,
          facultyPreferences: facultyPreferences,
          facultyLabAssignments: facultyLabAssignmentsArray,
          creationMode: 'standard'
        };
        updatedCourses.push(labCourse);
      }
      
      setCourses(updatedCourses);
      setTempCourseData(null);
      setIsFacultyModalOpen(false);
    }
  };

  const handleDeleteCourse = (indexToDelete: number) => {
    const courseToDelete = courses[indexToDelete];
    if (!courseToDelete) return;

    const isTheoryCourse = !courseToDelete.name.endsWith(' Lab');

    if (isTheoryCourse) {
      // Deleting a theory course, so also delete its corresponding lab.
      const baseCourseName = getBaseCourseName(courseToDelete.name);
      const labCourseNameToDelete = `${baseCourseName} Lab`;
      setCourses(prevCourses => 
        prevCourses.filter(c => getBaseCourseName(c.name) !== baseCourseName)
      );
    } else {
      // Deleting a lab course.
      const labCourseName = courseToDelete.name;
      const theoryCourseName = getBaseCourseName(labCourseName);

      setCourses(prevCourses => {
        // First, update the parent theory course to remove lab assignments
        const updatedCourses = prevCourses.map(course => {
          if (course.name === theoryCourseName) {
            const { facultyLabAssignments, ...rest } = course;
            return rest;
          }
          return course;
        });

        // Then, filter out the lab course itself
        return updatedCourses.filter(course => course.name !== labCourseName);
      });
    }
  };

  const handleTimeTableSubmit = (data: { semester: string; timing: 'morning' | 'evening' }) => {
    // Handle the submission of the time table data
    setTimeTableModalOpen(false);
  };

  // Handle TimeTableSlot submission
  const handleTimeTableSlotSubmit = (data: { 
    courseName: string; 
    selectedSlots: string[]; 
    credits: number;
  }) => {
    let updatedCourses = [...courses];

    if (editingCourseIndex !== null) {
      updatedCourses[editingCourseIndex] = { 
        ...updatedCourses[editingCourseIndex],
        name: data.courseName, 
        slots: data.selectedSlots, 
        credits: data.credits,
      };
      setEditingCourseIndex(null);
    } else {
      const newTheoryCourse: Course = {
        name: data.courseName, 
        slots: data.selectedSlots, 
        credits: data.credits,
        colorIndex: tempColorIndex,
        creationMode: 'custom',
      };
      updatedCourses.push(newTheoryCourse);
    }
    
    setCourses(updatedCourses);
  };

  // Add getAllSelectedSlots function
  const getAllSelectedSlots = (excludeIndex: number | null = null): string[] => {
    let baseNameToExclude: string | null = null;
    if (excludeIndex !== null && courses[excludeIndex]) {
      baseNameToExclude = getBaseCourseName(courses[excludeIndex].name);
    }

    return courses.reduce((accSlots: string[], course) => {
      if (baseNameToExclude && getBaseCourseName(course.name) === baseNameToExclude) {
        return accSlots;
      }
    
      let slotsForThisCourse: string[] = [...course.slots]; // Default to general course slots

      if (course.name.endsWith(' Lab') &&
          course.facultyPreferences && course.facultyPreferences.length > 0 &&
          course.facultyLabAssignments) {
        const firstFacultyName = course.facultyPreferences[0];
        const firstFacultyLabAssignment = course.facultyLabAssignments.find(a => a.facultyName === firstFacultyName);
        
        if (firstFacultyLabAssignment && firstFacultyLabAssignment.slots.length > 0) {
          slotsForThisCourse = firstFacultyLabAssignment.slots; // Use specific slots
        }
        // If no specific assignment for the first faculty, it implicitly uses course.slots (from initialization)
      }
      return [...accSlots, ...slotsForThisCourse];
    }, []);
  };

  // Handle Edit Course
  const handleEditCourse = (index: number) => {
    setEditingCourseIndex(index);
    const courseToEdit = courses[index];
    // Open the appropriate modal based on the course's creationMode
    if (courseToEdit.creationMode === 'custom') {
      setIsTimeTableSlotModalOpen(true);
    } else { // 'standard' or undefined (for old data)
      setIsCourseModalOpen(true);
    }
  };

  // Handle Edit Faculty
  const handleEditFaculty = (index: number) => {
    setEditingCourseIndex(index);
    const course = courses[index];
    let initialAssignments;

    // Check if we are editing the theory part of a course that has a lab
    const labCourseName = `${getBaseCourseName(course.name)} Lab`;
    const labCourse = courses.find(c => c.name === labCourseName);

    if (course.name.endsWith(' Lab')) {
      // If editing a lab directly, get assignments from it.
      initialAssignments = course.facultyLabAssignments;
    } else {
      // If editing a theory course, get assignments from its associated lab course, if it exists.
      initialAssignments = labCourse?.facultyLabAssignments;
    }
    
    // If no assignments found yet, check the theory course itself (for slash-command autofill cases)
    if (!initialAssignments && course.facultyLabAssignments) {
        initialAssignments = course.facultyLabAssignments;
    }
    
    const tempData = {
      courseName: course.name,
      selectedSlots: course.slots,
      credits: course.credits,
      facultyLabAssignments: initialAssignments 
        ? new Map(initialAssignments.map(a => [a.facultyName, a.slots]))
        : new Map(),
    };
    setTempCourseData(tempData);
    setTempColorIndex(course.colorIndex);

    // If it's a lab course, open the lab slot modal directly
    if (course.name.endsWith(' Lab')) {
      const theoryCourse = courses.find(c => c.name === course.name.replace(' Lab', ''));
      if (theoryCourse) {
        setTempCourseData({
          ...tempData,
          courseName: `${theoryCourse.name} Edit Lab`,
          selectedSlots: theoryCourse.slots,
          credits: theoryCourse.credits,
        });
      }
      setIsFacultyModalOpen(true);
    } else {
      setTempCourseData({
        ...tempData,
        courseName: `${course.name} Edit Faculty`
      });
      setIsFacultyModalOpen(true);
    }
  };

  // Handle Add Course
  const handleAddCourse = () => {
    setEditingCourseIndex(null);
    setTempColorIndex(getNextColorIndex(courses));
    // Open the appropriate modal based on selected preference
    if (preferredSlot === 'custom') {
      setIsTimeTableSlotModalOpen(true);
    } else {
      setIsCourseModalOpen(true);
    }
  };

  // Handle Add Lab
  const handleAddLab = (index: number) => {
    const theoryCourse = courses[index];
    setEditingCourseIndex(index);
    
    const initialAssignments = theoryCourse.facultyLabAssignments;

    const tempData = {
      courseName: `${theoryCourse.name} Add Lab`,
      selectedSlots: theoryCourse.slots,
      credits: theoryCourse.credits,
      facultyLabAssignments: initialAssignments
        ? new Map(initialAssignments.map(a => [a.facultyName, a.slots]))
        : new Map(),
    };
    setTempCourseData(tempData);
    setTempColorIndex(theoryCourse.colorIndex);
    setIsFacultyModalOpen(true);
  };

  // Add exportDataToCSV function
  const exportDataToCSV = (userName?: string, message?: string): void => {
    const currentCampus = localStorage.getItem('ffcs_planner_campus') || 'Vellore Campus';
    const exportCsvHeaders = ['campus', 'courseName', 'slots', 'credits', 'colorIndex', 'facultyPreferences', 'preferredSlot', 'creationMode', 'facultyLabAssignments'];
    const csvData = courses.map(course => ({
      campus: currentCampus,
      courseName: course.name,
      slots: course.slots.join('|'),
      credits: course.credits,
      colorIndex: course.colorIndex,
      facultyPreferences: course.facultyPreferences?.map((fp: string) => fp.toLowerCase()).join('|') || '',
      preferredSlot: preferredSlot,
      creationMode: course.creationMode || 'standard',
      facultyLabAssignments: course.facultyLabAssignments
        ?.map(assignment => `${assignment.facultyName.toLowerCase()}:${assignment.slots.join('-')}`)
        .join(';') || ''
    }));

    const csvContent = [
      exportCsvHeaders.join(','),
      ...csvData.map(row => 
        exportCsvHeaders.map(header => row[header as keyof typeof row]).join(',')
      )
    ].join('\n');

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create a link element to trigger the download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `ffcs-timetable-${currentCampus.toLowerCase().replace(' ', '-')}-${timestamp}${message ? '-' + message : ''}.csv`);
    document.body.appendChild(link);
    
    // Trigger the download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle tab close warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dontShowCloseWarning && courses.length > 0) {
        // Show browser's default confirmation dialog
        e.preventDefault();
        e.returnValue = 'Changes you made may not be saved.';
        
        // Show our custom modal
        setIsCloseWarningModalOpen(true);
        return e.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !dontShowCloseWarning && courses.length > 0) {
        setIsCloseWarningModalOpen(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [courses, dontShowCloseWarning]);

  // Handle closing the warning modal
  const handleCloseWarningModal = () => {
    setIsCloseWarningModalOpen(false);
  };

  const handleDontShowAgainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setDontShowCloseWarning(newValue);
    localStorage.setItem(DONT_SHOW_CLOSE_WARNING_KEY, newValue.toString());
  };

  // Add function to check if any modal is open
  const isAnyModalOpen = () => {
    return isCourseModalOpen || 
           isTimeTableModalOpen || 
           isFacultyModalOpen || 
           isTimeTableSlotModalOpen || 
           isImportModalOpen || 
           isExportModalOpen || 
           isResetModalOpen || 
           isCloseWarningModalOpen;
  };

  // Add click outside handler for info popups
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (infoPopupOpen && !target.closest('.info-popup-trigger')) {
        setInfoPopupOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [infoPopupOpen]);

  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        const courseBeingEdited = editingCourseIndex !== null ? courses[editingCourseIndex] : null;
        let isLabPresent = false;
        if (courseBeingEdited && !courseBeingEdited.name.endsWith(' Lab')) {
            const labCourseName = `${getBaseCourseName(courseBeingEdited.name)} Lab`;
            isLabPresent = courses.some(c => c.name === labCourseName);
        }

        const getSortableSlotKey = (slot: string | undefined): string => {
          if (!slot) return '4_'; // Should be last
        
          // Theory slots (e.g., A1, B2, TAA1)
          const theoryMatch = slot.match(/^([A-Z]+)([12])$/);
          if (theoryMatch) {
            const prefix = theoryMatch[1];
            const session = theoryMatch[2]; // '1' or '2'
            return `${session}_${prefix}`;
          }
        
          // Lab slots (e.g., L1, L31)
          const labMatch = slot.match(/^L(\d+)$/);
          if (labMatch) {
            const num = parseInt(labMatch[1]);
            return `3_${num.toString().padStart(3, '0')}`; // Pad to handle L1 vs L10
          }
        
          // Fallback for any other slot format (like custom slots)
          return `4_${slot}`;
        };

        const coursesWithOriginalIndex = courses.map((course, index) => ({
          ...course,
          originalIndex: index,
          baseName: getBaseCourseName(course.name),
          isLab: course.name.endsWith(' Lab'),
        }));
        
        coursesWithOriginalIndex.sort((a, b) => {
          const aSortSlot = getSortableSlotKey(a.isLab ? courses.find(c => c.name === a.baseName)?.slots[0] : a.slots[0]);
          const bSortSlot = getSortableSlotKey(b.isLab ? courses.find(c => c.name === b.baseName)?.slots[0] : b.slots[0]);

          if (a.baseName === b.baseName) {
            return a.isLab ? 1 : -1;
          }

          if (aSortSlot < bSortSlot) return -1;
          if (aSortSlot > bSortSlot) return 1;

          return a.baseName.localeCompare(b.baseName);
        });

        const sortedCourses = coursesWithOriginalIndex.map(({ originalIndex, baseName, isLab, ...course }) => course);

        return (
          <div className="space-y-8">
            {/* Semester Info and Course Cards */}
            <div>
              <div className="space-y-6">
                {/* Semester Input and Theory Preference in same row */}
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mt-8">
                  {/* Theory Slot Preference */}
                  <div>
                    <label className="block text-lg font-semibold text-black mb-2 flex items-center">
                      Slot Selection Mode
                      <div className="relative ml-2 flex items-center group">
                        <button 
                          onClick={(e) => {
                            if (window.innerWidth < 640) {
                              e.preventDefault();
                              setInfoPopupOpen(infoPopupOpen === 'slotMode' ? null : 'slotMode');
                            }
                          }}
                          className="flex items-center translate-y-[1px]"
                        >
                          <AiOutlineInfoCircle className="text-gray-500 hover:text-gray-700 cursor-help w-4 h-4" />
                        </button>
                        <div 
                          className="absolute left-[calc(100%+8px)] top-1/2 transform -translate-y-1/2 w-64 bg-black text-white text-sm rounded-lg p-3 transition-opacity duration-200 pointer-events-none z-50 opacity-0 sm:group-hover:opacity-100"
                          style={{
                            opacity: window.innerWidth < 640 && infoPopupOpen === 'slotMode' ? 1 : undefined
                          }}
                        >
                          <div className="flex flex-col gap-2">
                            <p>Standard mode is a guided mode of course selection with faculty preference.</p>
                            <p>Custom mode allows unrestricted slot selection without any faculty preferences.</p>
                          </div>
                          <div className="absolute left-[-6px] top-1/2 transform -translate-y-1/2 w-0 h-0 border-r-[6px] border-r-black border-y-[6px] border-y-transparent"></div>
                        </div>
                      </div>
                    </label>
                    <div className="bg-gray-100 rounded-lg p-1 inline-flex items-center h-12">
                      <button
                        onClick={() => {
                          setPreferredSlot('standard');
                          setIsTimeTableSlotModalOpen(false);
                          setIsCourseModalOpen(false);
                        }}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all h-full ${ 
                          preferredSlot === 'standard'
                            ? 'bg-white shadow-sm text-black' 
                            : 'text-black' 
                        }`}
                      >
                        <AiOutlineMenu className="text-black" />
                        <span className="text-black">Standard</span>
                      </button>
                      <div className="w-px h-5 bg-gray-300"></div>
                      <button
                        onClick={() => {
                          setPreferredSlot('custom');
                          setIsCourseModalOpen(false);
                          setIsTimeTableSlotModalOpen(false);
                        }}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all h-full ${ 
                          preferredSlot === 'custom'
                            ? 'bg-white shadow-sm text-black' 
                            : 'text-black'  
                        }`}
                      >
                        <AiOutlineSetting className="mr-1 text-black" size={16} />
                        <span className="text-black">Custom</span>
                      </button>
                    </div>
                  </div>
                   
                  {/* Total Credits Counter */}
                  <div className="w-full sm:w-auto mt-2 sm:mt-8">
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                      <div className="bg-gray-100 px-4 py-2 rounded-lg flex items-center h-12">
                        <span className="text-2xl font-bold text-gray-800">
                          {courses.reduce((total, course) => total + course.credits, 0)}
                        </span>
                        <span className="ml-2 text-sm text-gray-600">credits</span>
                      </div>

                      {/* Reset Button */}
                      <button
                        onClick={handleResetTable}
                        className={`px-4 py-2 rounded-lg flex items-center space-x-2 h-12 ${
                          courses.length > 0 
                            ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        } transition-colors`}
                        disabled={courses.length === 0}
                        title="Reset all courses"
                      >
                        <VscDebugRestart size={20} />
                        <span className="font-medium">Reset</span>
                      </button>

                      {/* Color Palette Selector */}
                      <div className="relative" ref={paletteDropdownRef}>
                        <button
                          onClick={() => setIsPaletteDropdownOpen(!isPaletteDropdownOpen)}
                          className="flex items-center space-x-2 h-12 text-black hover:text-gray-600 transition-colors group"
                          title="Change color palette"
                        >
                          <AiOutlineBgColors size={20} />
                          <span className="font-medium">
                            Palette: {PALETTES[selectedPalette].name}
                          </span>
                          <IoChevronDown className="opacity-0 group-hover:opacity-100 transition-opacity" size={16} />
                        </button>
                        {isPaletteDropdownOpen && (
                          <div
                            className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50"
                          >
                            <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                            <div className="p-4">
                              <h3 className="text-sm font-semibold text-gray-800 mb-3">Color Palette</h3>
                              <div className="space-y-3">
                                {Object.entries(PALETTES).map(([key, palette]) => (
                                  <button
                                    key={key}
                                    onClick={() => {
                                      setSelectedPalette(key as keyof typeof PALETTES);
                                      setIsPaletteDropdownOpen(false);
                                    }}
                                    className={`w-full text-left p-2 rounded-lg transition-colors flex items-center justify-between ${
                                      selectedPalette === key 
                                        ? 'bg-gray-100' 
                                        : 'hover:bg-gray-100'
                                    }`}
                                  >
                                    <span className="font-medium text-sm text-gray-700">
                                      {palette.name}
                                    </span>
                                    <div className="flex items-center space-x-1.5">
                                      {palette.preview.map((color, i) => (
                                        <div
                                          key={i}
                                          className="w-4 h-4 rounded-full"
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Course Cards */}
                <CourseCards 
                  courses={sortedCourses}
                  onEditCourse={(index) => handleEditCourse(coursesWithOriginalIndex[index].originalIndex)}
                  onEditFaculty={(index) => handleEditFaculty(coursesWithOriginalIndex[index].originalIndex)}
                  onDeleteCourse={(index) => handleDeleteCourse(coursesWithOriginalIndex[index].originalIndex)}
                  onAddCourse={handleAddCourse}
                  onAddLab={(index) => handleAddLab(coursesWithOriginalIndex[index].originalIndex)}
                  blockedSlots={getAllSelectedSlots()}
                  palette={selectedPalette}
                />
              </div>
            </div>

            {/* Timetable Display Area */}
            <div className="flex items-center mb-4 mt-8">
              <AiOutlineCalendar size={20} className="text-black mr-2" />
              <h2 className="text-xl font-semibold text-black">Time Table</h2>
            </div>
            <div className="shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-200 rounded-xl overflow-hidden">
              <TimeTable courses={courses} palette={selectedPalette} />
            </div>

            {/* Course Selector Modal */}
            <CourseSlotSelector
              isOpen={isCourseModalOpen}
              onClose={() => {
                setIsCourseModalOpen(false);
                setEditingCourseIndex(null);
              }}
              onSubmit={handleCourseSubmit}
              onAddFaculty={handleAddFaculty}
              existingSlots={getAllSelectedSlots().filter((slot: string) => 
                editingCourseIndex === null || !courses[editingCourseIndex].slots.includes(slot)
              )}
              editingCourse={editingCourseIndex !== null ? courses[editingCourseIndex] : undefined}
              palette={selectedPalette}
              colorIndex={editingCourseIndex !== null ? courses[editingCourseIndex].colorIndex : tempColorIndex}
            />

            <TimeTableModal
              isOpen={isTimeTableModalOpen}
              onClose={() => setTimeTableModalOpen(false)}
              onSubmit={handleTimeTableSubmit}
              existingSlots={getAllSelectedSlots()}
              palette={selectedPalette}
              courses={courses}
            />

            {/* TimeTableSlot Modal */}
            <TimeTableSlotSelector
              isOpen={isTimeTableSlotModalOpen}
              onClose={() => {
                setIsTimeTableSlotModalOpen(false);
                setEditingCourseIndex(null);
              }}
              onSubmit={handleTimeTableSlotSubmit}
              editingCourse={editingCourseIndex !== null ? courses[editingCourseIndex] : undefined}
              otherCoursesData={courses.filter((course, index) => 
                editingCourseIndex === null || index !== editingCourseIndex
              )}
              slotConflictPairs={slotConflictPairs}
              palette={selectedPalette}
              colorIndex={editingCourseIndex !== null ? courses[editingCourseIndex].colorIndex : tempColorIndex}
            />

            {/* Faculty Preference Modal */}
            {isFacultyModalOpen && tempCourseData && (
              <FacultyPreferenceModal
                isOpen={isFacultyModalOpen}
                onClose={(currentFacultyPreferences, currentLabAssignments) => {
                  // If coming from back button (no preferences passed), reopen course modal
                  if (!currentFacultyPreferences) {
                    setIsFacultyModalOpen(false);
                    setIsCourseModalOpen(true);
                  } else {
                    // Normal close (confirm or force close)
                    setIsFacultyModalOpen(false);
                    setTempCourseData(null);
                    setEditingCourseIndex(null);
                  }
                }}
                onForceClose={() => {
                  setIsFacultyModalOpen(false);
                  setTempCourseData(null);
                  setEditingCourseIndex(null);
                }}
                onSubmit={handleFacultyPreferenceSubmit}
                courseName={`${tempCourseData.courseName} ${tempCourseData.selectedSlots.join('+')}`}
                courseCredits={tempCourseData.credits}
                initialFacultyPreferences={
                  editingCourseIndex !== null && courses[editingCourseIndex] 
                    ? courses[editingCourseIndex].facultyPreferences 
                    : tempCourseData.courseName.includes('Add Lab') && tempCourseData.facultyPreferences
                      ? tempCourseData.facultyPreferences
                      : []
                }
                initialFacultyLabAssignments={tempCourseData.facultyLabAssignments}
                allCurrentlyUsedSlots={getAllSelectedSlots(editingCourseIndex)}
                slotConflictPairs={slotConflictPairs}
                isLabCourseAssociated={isLabPresent}
                palette={selectedPalette}
                colorIndex={tempColorIndex}
                hasLabComponent={tempCourseData.hasLabComponent}
                isSlashCommandSelection={tempCourseData.isSlashCommandSelection}
                allFacultyData={facultyData}
              />
            )}
          </div>
        );
      case 'faculty':
        return <FacultyList data={facultyData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hidden file input for CSV import */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={importDataFromCSV}
        accept=".csv"
        className="hidden"
        id="csv-file-input"
      />
      
      {/* Main Content - Fixed width and centered */}
      <div className={`flex-grow pt-24 mx-auto w-full max-w-screen-2xl transition-all duration-300 ease-in-out px-4 sm:px-6 md:px-8 lg:px-20`}>
        <div className="w-full mx-auto py-4">
          {renderContent()}
        </div>
      </div>

      {/* Floating Help Button with Arc Menu */}
      <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-[5rem] z-50">
        <div className="relative" ref={helpMenuRef}>
          {/* Arc Menu Items */}
          {isHelpMenuOpen && (
            <>
              {/* How to Use Button - 0 degrees */}
              <a
                href="https://aneezhussain.com/how-to-use-f2cs-planner"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute arc-menu-item position-1 info-popup-trigger group"
                onClick={(e) => {
                  if (window.innerWidth < 640 && !isAnyModalOpen()) {
                    e.preventDefault();
                    if (infoPopupOpen === 'howto') {
                      window.open('https://aneezhussain.com/how-to-use-f2cs-planner', '_blank');
                      setInfoPopupOpen(null);
                    } else {
                      setInfoPopupOpen('howto');
                    }
                  }
                }}
              >
                <div className="bg-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
                  <AiOutlineInfoCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-gray-900 transition-colors duration-200" />
                </div>
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full ml-[-12px] bg-black text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap z-50 transition-all duration-200"
                  style={{
                    opacity: 1,
                    transform: 'translate(calc(-100% - 0.5rem), -50%)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                  }}>
                  How to use
                  <div className="absolute top-1/2 right-[-4px] transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black border-y-[6px] border-y-transparent"></div>
                </div>
              </a>

              {/* Feedback Button - 45 degrees */}
              <button
                onClick={() => {
                  if (!isAnyModalOpen()) {
                    if (window.innerWidth < 640) {
                      if (infoPopupOpen === 'feedback') {
                        window.location.href = 'mailto:aneezhussain.protomail.com';
                        setInfoPopupOpen(null);
                      } else {
                        setInfoPopupOpen('feedback');
                      }
                    } else {
                      window.location.href = 'mailto:aneezhussain.protomail.com';
                    }
                  }
                }}
                className="absolute arc-menu-item position-2 info-popup-trigger group"
              >
                <div className="bg-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
                  <AiOutlineMail className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-gray-900 transition-colors duration-200" />
                </div>
                <div className="absolute left-0 top-0 transform -translate-x-full -translate-y-1/4 ml-[-12px] bg-black text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap z-50 transition-all duration-200"
                  style={{
                    opacity: 1,
                    transform: 'translate(calc(-80% - 0.75rem), -75%)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                  }}>
                  Feedback
                  <div className="absolute top-1/2 right-[-4px] transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black border-y-[6px] border-y-transparent"></div>
                </div>
              </button>

              {/* About Creator Button - 90 degrees */}
              <a
                href="https://aneezhussain.com"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute arc-menu-item position-3 info-popup-trigger group"
                onClick={(e) => {
                  if (window.innerWidth < 640 && !isAnyModalOpen()) {
                    e.preventDefault();
                    if (infoPopupOpen === 'creator') {
                      window.open('https://aneezhussain.com', '_blank');
                      setInfoPopupOpen(null);
                    } else {
                      setInfoPopupOpen('creator');
                    }
                  }
                }}
              >
                <div className="bg-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
                  <AiOutlineUser className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700 group-hover:text-gray-900 transition-colors duration-200" />
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -translate-y-2 mb-2 bg-black text-white px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap z-50 transition-all duration-200"
                  style={{
                    opacity: 1,
                    transform: 'translate(-50%, calc(-20% - 0.60rem))',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                  }}>
                  About Me
                  <div className="absolute bottom-[-4px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-t-[6px] border-t-black border-x-[6px] border-x-transparent"></div>
                </div>
              </a>
            </>
          )}

          {/* Main Help Button */}
          <button
            onClick={() => setIsHelpMenuOpen(!isHelpMenuOpen)}
            className={`bg-black hover:bg-gray-900 text-white rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 ${isHelpMenuOpen ? 'rotate-180' : ''}`}
          >
            <AiOutlineQuestionCircle className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
      </div>

      <style>
        {`
          .arc-menu-item {
            opacity: 0;
            transform: translate(0, 0) scale(0.8);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .position-1 {
            /* 0 degrees - pure horizontal */
            transform: translate(-4.5rem, 0) scale(1);
            opacity: 1;
            transition-delay: 0s;
          }

          .position-2 {
            /* 45 degrees - equal x and y */
            transform: translate(-3.2rem, -3.2rem) scale(1);
            opacity: 1;
            transition-delay: 0.1s;
          }

          .position-3 {
            /* 90 degrees - pure vertical */
            transform: translate(0, -4.5rem) scale(1);
            opacity: 1;
            transition-delay: 0.2s;
          }

          @media (min-width: 640px) {
            .sm\\:group-hover\\:-translate-x-full-4 {
              transform: translate(calc(-100% - 1rem), -50%);
            }

            .sm\\:group-hover\\:-translate-y-4 {
              transform: translate(-50%, calc(-100% - 1rem));
            }
          }
        `}
      </style>

      {/* Footer */}
      <footer className="bg-white py-3 sm:py-4 mt-auto pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <p className="text-sm sm:text-base text-black">Made with ❤️ for Vitians</p>
          </div>
        </div>
      </footer>

      {/* Export Modal */}
      {isExportModalOpen && (
        <ExportModal 
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          exportDataToCSV={exportDataToCSV}
          triggerRef={exportButtonRef}
        />
      )}

      {/* Import Modal */}
      {isImportModalOpen && (
        <ImportPopover
          isOpen={isImportModalOpen}
          onClose={() => {
            setIsImportModalOpen(false);
            setImportError(undefined);
          }}
          triggerRef={importButtonRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragging={isDragging}
          importError={importError}
        />
      )}

      {/* Reset Confirmation Modal */}
      {isResetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4 text-red-500">
              <AiOutlineWarning className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-center mb-4">Reset Timetable</h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to reset your timetable? This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setIsResetModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Warning Modal */}
      {isCloseWarningModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-center mb-4 text-blue-500">
              <AiOutlineInfoCircle className="w-12 h-12" />
            </div>
            <h2 className="text-2xl font-bold text-center mb-4">Data Storage Information</h2>
            <div className="text-gray-600 text-center space-y-3 mb-6">
              <p>
                Your timetable data is safely stored in your browser's cache.
              </p>
              <p>
                Unless you clear your browser cache, your data will remain available the next time you visit.
              </p>
              <p className="text-sm text-gray-500 italic">
                You can always export your timetable to save it externally.
              </p>
            </div>
            <div className="flex flex-col items-center space-y-4">
              <label className="flex items-center space-x-2 text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontShowCloseWarning}
                  onChange={handleDontShowAgainChange}
                  className="form-checkbox h-4 w-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                />
                <span>Don't show this message again</span>
              </label>
              <div className="flex space-x-3">
                <button
                  onClick={handleCloseWarningModal}
                  className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium transition-colors"
                >
                  Got it
                </button>
                <button
                  onClick={() => {
                    handleCloseWarningModal();
                    exportButtonRef.current?.click();
                  }}
                  className="px-6 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 font-medium transition-colors"
                >
                  Export Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 
